import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { PlusIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert } from "../../types/alert";
import EditAlertDrawer from "./components/EditAlertDrawer";
import CreateAlertDrawer from "./components/CreateAlertDrawer";
import DeleteConfirmationModal from "../../components/modal/DeleteConfirmationModal";
import { useLocationStore } from "../../store/useLocationStore";
import { useDeleteAlert, useGetLocationAlerts } from "../../queries/alerts";
import { convertDate } from "../../utils/string";
import { useIsAdmin } from "../../utils/auth";

const getConditionBadge = (condition: Alert["condition"]) => {
  switch (condition) {
    case "ABOVE":
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400">
          Above
        </Badge>
      );
    case "ABOVE_EQUAL":
      return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400">
          Above or Equal
        </Badge>
      );
    case "BELOW":
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400">
          Below
        </Badge>
      );
    case "BELOW_EQUAL":
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400">
          Below or equal
        </Badge>
      );
    case "BETWEEN":
      return (
        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400">
          Between
        </Badge>
      );
    case "EQUAL":
      return (
        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400">
          Equal
        </Badge>
      );
    default:
      return <Badge variant="outline">Unknown</Badge>;
  }
};

export default function AlertsListPage() {
  const isAdmin = useIsAdmin();
  const { selectedLocationId } = useLocationStore();
  const { data } = useGetLocationAlerts(selectedLocationId);
  const { mutate: deleteAlert } = useDeleteAlert();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);

  useEffect(() => {
    if (data) {
      setAlerts(data);
    }
  }, [data]);

  const handleCreateAlert = () => {
    setIsCreateModalOpen(true);
  };

  const handleEditAlert = (alert: Alert) => {
    setSelectedAlert(alert);
    setIsEditModalOpen(true);
  };

  const handleDeleteAlert = (alert: Alert) => {
    setAlertToDelete(alert);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (alertToDelete) {
      setAlerts(alerts.filter((a) => a.id !== alertToDelete.id));
      setAlertToDelete(null);
      setIsDeleteModalOpen(false);
      deleteAlert(alertToDelete.id);
    }
  };

  const handleSaveAlert = (updatedAlert: Alert) => {
    setAlerts(alerts.map((a) => (a.id === updatedAlert.id ? updatedAlert : a)));
    setIsEditModalOpen(false);
    setSelectedAlert(null);
  };

  const handleCreateAlertSuccess = () => {
    // The query will be invalidated by the CreateAlertDrawer, so we just need to close the modal
    setIsCreateModalOpen(false);
  };

  return (
    <div className="h-full bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/alerts")}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Manage Alerts
          </h1>
        </div>

        {isAdmin && (
          <Button
            variant="outline"
            onClick={handleCreateAlert}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            Create New Alert
          </Button>
        )}
      </div>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        View, edit, and manage your alert configurations
      </p>

      {/* Alerts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Alert Rules ({alerts.length})
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Alert Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Device
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Trigger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Recipients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Last Triggered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {alerts.map((alert) => (
                <tr
                  key={alert.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {alert.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Created {new Date(alert.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Icon icon="mdi:chip" className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {alert.deviceName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {alert.paramName} {alert.condition.toLowerCase()}{" "}
                      {alert.threshold} {alert.paramUnit}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Primary: {alert.primaryCooldown}s, Escalation:{" "}
                      {alert.cooldown}s
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {alert.primaryEmails.length +
                        alert.secondaryEmails.length}{" "}
                      recipient
                      {alert.primaryEmails.length +
                        alert.secondaryEmails.length >
                      1
                        ? "s"
                        : ""}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Primary: {alert.primaryEmails.length}, Secondary:{" "}
                      {alert.secondaryEmails.length}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getConditionBadge(alert.condition)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {alert.lastTriggered
                      ? convertDate(alert.lastTriggered)
                      : "Never"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        className={`p-1 rounded transition-colors ${
                          alert.condition === "ABOVE"
                            ? "text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300"
                            : "text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                        }`}
                        title={
                          alert.condition === "ABOVE"
                            ? "Change to Below"
                            : "Change to Above"
                        }
                      >
                        <Icon
                          icon={
                            alert.condition === "ABOVE"
                              ? "heroicons:arrow-down"
                              : "heroicons:arrow-up"
                          }
                          className="w-4 h-4"
                        />
                      </button>
                      <button
                        onClick={() => handleEditAlert(alert)}
                        className="p-1 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
                        title="Edit Alert"
                      >
                        <Icon icon="heroicons:pencil" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteAlert(alert)}
                        className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                        title="Delete Alert"
                      >
                        <Icon icon="heroicons:trash" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {alerts.length === 0 && (
          <div className="text-center py-24">
            <Icon
              icon="mdi:bell-off-outline"
              className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4"
            />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No alerts configured
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first alert to get started with notifications.
            </p>
            <Button onClick={handleCreateAlert}>Create Alert</Button>
          </div>
        )}
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateAlertDrawer
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={handleCreateAlertSuccess}
        />
      )}

      {isEditModalOpen && selectedAlert && (
        <EditAlertDrawer
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAlert(null);
          }}
          alert={selectedAlert}
          onSave={handleSaveAlert}
        />
      )}

      {isDeleteModalOpen && alertToDelete && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setAlertToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Delete Alert"
          itemName={alertToDelete.name}
          itemType="alert"
          customMessage={`Are you sure you want to delete alert "${alertToDelete.name}"? This action cannot be undone and all associated notifications will be stopped immediately.`}
        />
      )}
    </div>
  );
}
